import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { motion } from 'framer-motion';
import './assets/css/home.css';
import HomePageArticleContent from './components/HomePageArticleContent';
import { Helmet } from 'react-helmet';
import HomeHero from './components/hero/HomeHero';
import HomeStatement from './components/statement/HomeStatement';
import HomeQuickLinks from './components/HomeQuickLinks';
import sbFish from './assets/images/SBFish.png';
import checked from './assets/images/check-mark.svg';
import sbMobile from './assets/images/iPhone Mockups 1.webp';
import fisheryType1 from './assets/images/fishery_type_1.webp';
import fisheryType2 from './assets/images/fishery_type_2.webp';
import fisheryType3 from './assets/images/fishery_type_3.webp';
import fisheryType4 from './assets/images/fishery_type_4.webp';
import HomePageVenueContent from './components/HomePageVenueContent';
import LiveCatchReports from './components/LiveCatchReports';
import HotFishingPicks from './components/HotFishingPicks';
import { useGetPremiumFisheriesQuery } from './premiumFisheriesSlice';
import { Link } from 'react-router-dom';
import SBProBannerAd from './components/SBProBannerAd';
import WatercraftHomepageBanner from './components/watercraft';
import { useGetBrandsQuery } from '../brands/brandsSlice';
import { shuffleArray } from '../../utils/arrayUtils';
import { useGetArticlesQuery } from '../articles/articlesSlice';
import { buildSearchTagPath, SEARCH_TAG_IDS } from '../search/utils/searchUrls';
const imagePaths = {
  listLakeBg: require('./assets/images/List-Lake-bg.webp'),
  infoicon1: require('./assets/images/info-icon1.svg').default,
  infoicon2: require('./assets/images/info-icon2.svg').default,
  infoicon3: require('./assets/images/info-icon3.svg').default,
  infoicon4: require('./assets/images/info-icon4.svg').default,
};

const MotionLink = motion(Link);

function InfoBlock({ icon, title, children }) {
  return (
    <div className={'home_info_block mg-bottom-20'}>
      <span
        className={'home_info_block__icon sb-blue row-center fs-25 pd-bottom-5'}
      >
        <img src={icon} alt='info-icons' />
      </span>
      <h4 className='home_info_block__title sb-blue-900 txt-center fs-22 gBold mg-bottom-5'>
        {title}
      </h4>
      <p className='gSemiBold grey-800 fs-18 txt-center'>{children}</p>
    </div>
  );
}

export default function Home() {
  const [articlesNearViewport, setArticlesNearViewport] = useState(false);
  const { data: articlesData, isLoading: articlesLoading } =
    useGetArticlesQuery(undefined, { skip: !articlesNearViewport });

  const { data } = useGetBrandsQuery();

  const {
    data: premiumFisheriesData,
    isFetching: isPremiumFisheriesFetching,
    isError: isPremiumFisheriesError,
  } = useGetPremiumFisheriesQuery();
  const premiumListings = Array.isArray(premiumFisheriesData)
    ? premiumFisheriesData
    : [];
  const showWhySwimbookerSection =
    isPremiumFisheriesError ||
    (!isPremiumFisheriesFetching && premiumListings.length === 0);

  const shuffledBrands = useMemo(() => {
    if (!data) {
      return data;
    }

    const tiers = shuffleArray(data);

    return tiers.slice(0, 4);
  }, [data]);

  const variantBottom = {
    visible: {
      y: 0,
      transition: {
        type: 'ease',
        bounce: 0.3,
        duration: 0.4,
      },
    },
    hidden: { y: '40vh' },
  };

  return (
    <div>
      <Helmet>
        <meta charSet='utf-8' />
        <meta
          name='description'
          content='swimbooker provides anglers the best places to fish nearby including some of the best carp fishing lakes in the country. Find fisheries near you via swimbooker'
        />
        <title>Book Fishing Lakes & Day Tickets Near You | swimbooker</title>
      </Helmet>

      <HomeHero />
      <HomeStatement />

      {showWhySwimbookerSection ? (
        <Row className={'home_info_blocks_container main_container_padding'}>
          <Col span={24} className={'main_container_padding__content'}>
            <h2 className='sb-blue-800 gSemiBold fs-3em txt-center mg-bottom-50'>
              Why <span className='sb-blue-900 gBold'>swimbooker?</span>
            </h2>
            <motion.div
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.1 }}
            >
              <Row
                gutter={16}
                className='justify-between info-box'
                style={{ overflow: 'hidden' }}
              >
                <Col xs={24} sm={12} lg={6} className=''>
                  <motion.div variants={variantBottom}>
                    <InfoBlock
                      icon={imagePaths.infoicon1}
                      title='No fees to book.'
                    >
                      Our platform is free to use with no additional fees to
                      book.
                    </InfoBlock>
                  </motion.div>
                </Col>
                <Col xs={24} sm={12} lg={6} className=''>
                  <motion.div variants={variantBottom}>
                    <InfoBlock
                      icon={imagePaths.infoicon2}
                      title='Massive selection.'
                    >
                      Easily find the perfect venue that completely suits your
                      requirements.
                    </InfoBlock>
                  </motion.div>
                </Col>
                <Col xs={24} sm={12} lg={6} className=''>
                  <motion.div variants={variantBottom}>
                    <InfoBlock
                      icon={imagePaths.infoicon3}
                      title='Track your angling.'
                    >
                      Upload catches, favourite lakes & earn badges to progress.
                    </InfoBlock>
                  </motion.div>
                </Col>
                <Col xs={24} sm={12} lg={6} className=''>
                  <motion.div variants={variantBottom}>
                    <InfoBlock
                      icon={imagePaths.infoicon4}
                      title='We’re here to help.'
                    >
                      Our incredible team is here to support in any capacity
                      that is required.
                    </InfoBlock>
                  </motion.div>
                </Col>
              </Row>
            </motion.div>
          </Col>
        </Row>
      ) : null}

      <HotFishingPicks />

      <WatercraftHomepageBanner />

      <div
        className='fs-20 gSemiBold white br20 mg-auto w-fit mg-bottom-30 mg-top-20'
        style={{ background: '#357EB9', padding: '2px 20px' }}
      >
        THE #1 FISHING PLATFORM
      </div>
      <div className='home_info_blocks_container main_container_padding'>
        <div className='fs-3em gSemiBold sb-blue-800 txt-center mg-bottom-20'>
          {' '}
          Something For <span className='gBold sb-blue-900'>Everyone...</span>
        </div>
        <Row
          gutter={[{ xs: 16, sm: 32, lg: 60 }, 20]}
          className='justify-between info-box'
          style={{ overflow: 'hidden' }}
        >
          <Col xs={24} sm={12} lg={6} className='mg-top-20'>
            <Link
              to={buildSearchTagPath(SEARCH_TAG_IDS.WATERCRAFT_TICKET)}
              className='lake-container display-block'
            >
              <div className='lake-img'>
                <img src={fisheryType1} alt='' loading='lazy' />
              </div>
              <div className='lake-title   gBold txt-center mg-top-30'>
                Watercraft Tickets
              </div>
            </Link>
          </Col>
          <Col xs={24} sm={12} lg={6} className='mg-top-20'>
            <Link
              to={buildSearchTagPath(SEARCH_TAG_IDS.LAKE_EXCLUSIVE)}
              className='lake-container display-block'
            >
              <div className='lake-img'>
                <img src={fisheryType2} alt='' loading='lazy' />
              </div>
              <div className='lake-title   gBold txt-center mg-top-30'>
                Lake Exclusives
              </div>
            </Link>
          </Col>
          <Col xs={24} sm={12} lg={6} className='mg-top-20'>
            <Link
              to={buildSearchTagPath(SEARCH_TAG_IDS.LODGES)}
              className='lake-container display-block'
            >
              <div className='lake-img'>
                <img src={fisheryType3} alt='' loading='lazy' />
              </div>
              <div className='lake-title   gBold txt-center mg-top-30'>
                Accommodation
              </div>
            </Link>
          </Col>
          <Col xs={24} sm={12} lg={6} className='mg-top-20'>
            <a
              href='https://holidays.swimbooker.com/'
              className='lake-container display-block'
            >
              <div className='lake-img'>
                <img src={fisheryType4} alt='' loading='lazy' />
              </div>
              <div className='lake-title   gBold txt-center mg-top-30'>
                European
              </div>
            </a>
          </Col>
        </Row>
      </div>
      <div className='main_container_padding'>
        <div className='main_container_padding__content'>
          <div className='all-in-one-container '>
            <Col xs={24} sm={24} lg={12}>
              <div className='text-wrapper'>
                <div
                  className='fs-3em gBold white mg-bottom-15'
                  style={{ lineHeight: 1.4, maxWidth: 450 }}
                >
                  <span className='gSemiBold sb-blue-200'>All you need in</span>{' '}
                  one place...
                  <img
                    src={sbFish}
                    alt=''
                    width={44}
                    height={20}
                    className='mg-left-30'
                  />
                </div>
                <div>
                  <div className='gSemiBold fs-20 white row-center mg-bottom-15'>
                    <img src={checked} alt='' className='mg-right-15' />
                    Book a massive selection of venues
                  </div>
                  <div className='gSemiBold fs-20 white row-center mg-bottom-15'>
                    <img src={checked} alt='' className='mg-right-15' />
                    Upload, store and enrich your catch reports
                  </div>
                  <div className='gSemiBold fs-20 white row-center mg-bottom-15'>
                    <img src={checked} alt='' className='mg-right-15' />
                    360° Virtual Tours & high quality media
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={24} lg={12}>
              <img
                src={sbMobile}
                alt='iphone'
                loading='lazy'
                className='phone-sb-image'
              />
            </Col>
          </div>
        </div>
      </div>
      <SBProBannerAd animated={true} />
      <Row>
        <Col span={24} className='main_container_padding'>
          <div className='main_container_padding__content'>
            <motion.div
              viewport={{ once: true, amount: 0.5 }}
              initial='hidden'
              whileInView='visible'
              className='w-full  home_brand_container'
            >
              <Row className='' gutter={[20, 10]}>
                <Col
                  xs={24}
                  md={24}
                  lg={12}
                  style={{ display: 'flex' }}
                  className='center'
                >
                  <motion.div className='w-full justify-center'>
                    <Row
                      className='mg-auto w-full'
                      gutter={[30, 10]}
                      style={{ maxWidth: 360 }}
                    >
                      {shuffledBrands?.map((brand, index) => (
                        <Col xs={24} sm={12} md={12}>
                          <MotionLink
                            to='/brands'
                            className='brand__logo_container'
                            initial={{ opacity: 0, scale: 0.8, y: 40 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{
                              duration: 0.5,
                              delay: index * 0.2,
                              ease: 'easeOut',
                            }}
                            viewport={{ once: true, amount: 0.5 }}
                          >
                            <img
                              src={brand?.primary_image ?? brand.logo_image}
                              alt='brands'
                              loading='lazy'
                            />
                          </MotionLink>
                        </Col>
                      ))}
                    </Row>
                  </motion.div>
                </Col>
                <Col
                  xs={24}
                  md={24}
                  lg={12}
                  className='white '
                  style={{ overflow: 'hidden' }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: 100, visible: '' }}
                    whileInView={{ opacity: 1, scale: 1, x: 0 }}
                    className='home_brand_texts'
                    transition={{
                      duration: 0.8,
                      ease: 'easeOut',
                    }}
                    viewport={{ once: true, amount: 0.5 }}
                  >
                    <div className='brand__title gBold'>
                      Take Advantage <span className='gRegular'>Of Our </span>
                      Brand Partnerships
                    </div>
                    <div className='fs-20 gBold brand__sub__title'>
                      <span className='sb-blue-100 gMedium'>
                        {' '}
                        swimbooker is working with some of the{' '}
                      </span>
                      best brands in the industry
                      <span className='sb-blue-100 gMedium'>
                        {' '}
                        to bring you{' '}
                      </span>
                      exclusive offers & discounts!
                    </div>
                    <Link
                      to='/brands'
                      className='brand__button white fs-18 gBold'
                    >
                      View all Brands
                    </Link>
                  </motion.div>
                </Col>
              </Row>
            </motion.div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={24} className='main_container_padding'>
          <div className='main_container_padding__content'>
            <HomePageVenueContent />
          </div>
        </Col>
      </Row>

      <motion.div
        viewport={{ once: true, margin: '600px 0px' }}
        onViewportEnter={() => setArticlesNearViewport(true)}
      >
        <Row>
          <Col
            span={24}
            className='main_container_padding'
            id='home-page-article'
          >
            <div className={'main_container_padding__content'}>
              <HomePageArticleContent
                featured={articlesData?.featured}
                articles={articlesData?.articles?.slice(1, 5)}
                loading={articlesLoading}
              />
            </div>
          </Col>
        </Row>
      </motion.div>
      <LiveCatchReports />
      <Row>
        <Col span={24} className='main_container_padding'>
          <div className='main_container_padding__content'>
            <div className='list-lake-container'>
              <div className='list-lake-text'>
                {' '}
                <div
                  style={{ lineHeight: 'normal', maxWidth: 400 }}
                  className='mg-bottom-15'
                >
                  List your lakes with us to save time and increase revenue
                </div>
                <Link
                  to='/swimbooker-fishery-manager'
                  className='list-lake-button sb-blue-bg-600'
                >
                  {' '}
                  Learn How
                </Link>
              </div>
              <img
                src={imagePaths.listLakeBg}
                alt=''
                loading='lazy'
                className='list-lake-bg-img'
              />
            </div>
          </div>
        </Col>
      </Row>

      <div className='quick-links-container-wrapper main_container_padding'>
        <div className={'main_container_padding__content w-full'}>
          <Row
            className={'quick-links-container'}
            gutter={{ md: 16, lg: 32, xl: 64 }}
          >
            <Col span={24}>
              <h2 className='fs-36 sb-blue-900 gSemiBold mg-bottom-20'>
                Quick Access Links
              </h2>
            </Col>
            <Col xs={24} md={12} className='quick-links-container__column'>
              <HomeQuickLinks
                title={'For Anglers'}
                links={[
                  {
                    title: 'Browse All Fisheries',
                    link: '/search',
                  },
                  { title: 'The Angling Social', link: '/articles' },
                  { title: 'Create a Free Account', link: '/join' },
                  {
                    title: 'Rate us on Trustpilot',
                    link: 'https://uk.trustpilot.com/review/www.swimbooker.com',
                  },
                ]}
              />
            </Col>
            <Col xs={24} md={12}>
              <HomeQuickLinks
                title={'For Fisheries'}
                links={[
                  { title: 'Fishery Management Login', link: '/admin/login' },
                  { title: 'Contact Form - Fishery', link: '/contact/fishery' },
                  { title: 'Claim Fishery Profile', link: '/contact/fishery' },
                  {
                    title: 'Why swimbooker For My Fishery?',
                    link: '/swimbooker-fishery-manager',
                  },
                ]}
              />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
